import { I18n } from '@aws-amplify/core';

import type { AdditionalItem, AdditionalItemsResults } from '@/hooks';

interface RadioOption {
	id: string;
	description: string;
}

export interface FieldRow {
	title: string;
	value: string | number;
}

export interface ListEntry {
	entryId: string;
	actionLabel: string;
	riskScore: number;
	fieldRows: FieldRow[];
}

interface UseSelectDependentInputsListProps {
	items: AdditionalItem;
	radioOptions: RadioOption[];
}

interface UseSelectDependentInputsListReturn {
	entries: ListEntry[];
	hasEntries: boolean;
	actionTitle: string;
}

function buildFieldRows(
	items: AdditionalItem,
	result: AdditionalItemsResults,
	radioOptions: RadioOption[]
): FieldRow[] {
	const gloveOption = radioOptions.find(({ id }) => id === result.additional_item_option_id_2);
	const gloveDescription = gloveOption ? I18n.get(gloveOption.description) : undefined;

	const candidates = [
		{ title: items.title_2, value: result.value_1 },
		{ title: items.title_3, value: gloveDescription },
		{ title: items.title_4, value: result.value_2 },
		{ title: items.title_5, value: result.value_3 },
		{ title: items.title_6, value: result.value_4 }
	];

	return candidates.filter(
		({ title, value }) => !!title && value !== undefined && value !== null && value !== ''
	) as FieldRow[];
}

export function useSelectDependentInputsList({
	items,
	radioOptions
}: Readonly<UseSelectDependentInputsListProps>): UseSelectDependentInputsListReturn {
	const results = items?.results ?? [];

	const entries: ListEntry[] = results.map((result) => ({
		entryId: result.id ?? items.id,
		actionLabel: result.description ? I18n.get(result.description) : '',
		riskScore: result.result ?? 0,
		fieldRows: buildFieldRows(items, result, radioOptions)
	}));

	const hasEntries = entries.length > 0;
	const actionTitle = items?.title_1 ?? '';

	return {
		entries,
		hasEntries,
		actionTitle
	};
}
